import { isIElementMeasurePolygon } from "@faro-lotv/ielement-types";
import { convertIElementToThreePosition, selectAncestor, selectIElement, selectIElementWorldMatrix } from "@faro-lotv/project-source";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { convertDistance } from "@utils/unit-utils";
import { getMarkupMeasurements } from "@utils/markups-utils";
import { Matrix4, Vector3 } from "three";

/** Where the length of a segment is shown along it, at its middle */
const SEGMENT_LABEL_POSITION = 0.5;

/** A value of the measurement, shown where it applies */

/** Measurement an annotation was placed on, to draw it in the preview */

/** @returns the length of every segment, at its middle */
function getMeasurementLabels(polygon, points, unitMeasurement) {
  // Measurements made on a 360 photo are estimated, which the side panel shows the same way
  const prefix = polygon.metaDataMap?.isEstimatedFrom2DPano ? "~" : "";
  const segmentEnds = polygon.isClosed && points.length > 2 ? [...points, points[0]] : points;
  return segmentEnds.slice(1).map((end, index) => {
    const start = segmentEnds[index];
    return {
      position: start.clone().lerp(end, SEGMENT_LABEL_POSITION).toArray(),
      text: `${prefix}${convertDistance(start.distanceTo(end), unitMeasurement)}`
    };
  });
}

/** @returns the measurement the annotation was placed on, or undefined if it is not on one */
export function selectMarkupMeasurement(markupId) {
  return state => {
    const markup = selectIElement(markupId)(state);
    const polygon = selectAncestor(markup, isIElementMeasurePolygon)(state);
    if (!polygon || polygon.points.length < 2) {
      return;
    }
    const worldMatrix4Tuple = selectIElementWorldMatrix(polygon.id)(state);
    const worldMatrix = new Matrix4().fromArray(worldMatrix4Tuple);
    // The points are in the left-handed Y-up space of the Project API
    const points = polygon.points.map(point => new Vector3().fromArray(convertIElementToThreePosition(point)).applyMatrix4(worldMatrix));
    const unitMeasurement = userSettingsSelector(state).unit_measurement?.value ?? "meters";
    const {
      length,
      area
    } = getMarkupMeasurements({
      measurePolygon: polygon,
      worldMatrix4Tuple,
      unitMeasurement
    });
    return {
      points: points.map(point => point.toArray()),
      isClosed: polygon.isClosed,
      labels: getMeasurementLabels(polygon, points, unitMeasurement),
      summary: points.length > 2 ? {
        position: points.reduce((center, point) => center.add(point), new Vector3()).divideScalar(points.length).toArray(),
        text: `${area ? `\u2206: ${area}; ` : ""}\u2211: ${length}`
      } : undefined
    };
  };
}